import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useEmployeeStatusApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

// Personel Statüleri (AKM POC §6.1) — TitlesPage aynası. Başlık yalnız DataTable `title`'ında;
// "Yeni Statü" tablonun içinde (canCreateNewRecord + newButtonLabel).
const Container = styled.div``;

const InfoNote = styled.div`
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0 0 12px; line-height: 1.4;
`;

const EMPTY_CONFIRM = { isOpen: false, ids: [], name: '' };

export default function EmployeeStatusesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite('EM');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(EMPTY_CONFIRM);
  const [deleting, setDeleting] = useState(false);
  const [skippedCount, setSkippedCount] = useState(0);

  const {
    objectSchema, fetching, loadFailed, columns: baseColumns, data, pagination, create, update, _delete,
  } = useEmployeeStatusApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('employeeStatus.title')}` });
  }, [t]);

  // Silme reddedilirse (409 employeeStatus.inUse — toast interceptor'da) onay penceresi AÇIK
  // kalır ve tablo yeniden yüklenmez: kullanıcı nedeni okuyup kendisi kapatır.
  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    const result = await _delete(deleteConfirm.ids);
    setDeleting(false);
    if (!result.ok) return;
    setSkippedCount(result.skippedCount);
    setDeleteConfirm(EMPTY_CONFIRM);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    if (!writable) return [];
    return [
      { type: 'edit', label: t('employeeStatus.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('employeeStatus.delete'), onClick: handleDelete },
    ];
  }, [t, writable, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('employeeStatus.edit') : t('employeeStatus.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('employeeStatus.delete')}
        message={t('employeeStatus.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(EMPTY_CONFIRM)}
        loading={deleting}
      />

      {loadFailed && (
        <InfoNote role="alert" data-testid="employee-status-load-failed">{t('employeeStatus.loadFailed')}</InfoNote>
      )}
      {skippedCount > 0 && (
        <InfoNote data-testid="employee-status-skipped">
          {t('employeeStatus.skippedInUse', { count: skippedCount })}
        </InfoNote>
      )}

      {useMemo(() => (
        <DataTable
          title={t('employeeStatus.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          newButtonLabel={t('employeeStatus.new')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={writable ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, writable,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
